/**
 * THE Birdwatch custom emoji, and the one home for its wire encoding.
 *
 * The emoji is not text — the picker inserts it as a Tiptap image node, so it
 * travels inside the message body as markdown (`BW_EMOJI_MARKDOWN`) and counts
 * against the channel's `maxMessageLength`. Three consumers depend on the exact
 * shape below, so change it in one place only:
 *
 * - `html-to-markdown.ts` writes the token; `markdown-to-html.ts` reads it back.
 * - `platform/src/lib/messages/send-service-message-email.ts` swaps it for a
 *   20x20 `<img>`, and `platform/src/lib/utils/push-notification.ts` for an owl.
 * - The two Flutter apps do the same (`apps/owner/lib/utils/string_util.dart`,
 *   `apps/field/.../comments_provider_utils.dart` in the `mobile-apps` repo).
 *
 * All five match the literal `data:image/png;base64,` prefix, which is why the
 * image stays INLINE rather than moving to a hosted URL: a hosted src would
 * silently stop matching and leak raw markdown into mobile chat bubbles until
 * every one of those apps shipped a new release.
 *
 * The image is therefore kept as small as the display size allows — 32x32 at 16
 * colours renders identically to the 128x128 original at the 16-20px it is drawn
 * at, while costing 755 characters of the body instead of 5,975. That figure is
 * the whole point of this module: at 5,975 a SINGLE emoji exceeded the 5,000-char
 * channel limit, silently disabling Send.
 */

/**
 * The `alt` every Birdwatch-emoji image carries in the MARKDOWN token.
 * `platform` and the Flutter apps key their substitutions off this exact string.
 */
export const BW_EMOJI_ALT = "bw-emoji";

/**
 * The `alt` the same image carries as a DOM node inside the editor.
 *
 * Deliberately different from `BW_EMOJI_ALT`: the clipboard sanitizer keeps only
 * images whose alt is exactly `"emoji"` (`extensions/event-handler.ts`), so an
 * image tagged `bw-emoji` would be discarded on copy-paste inside the composer.
 */
export const BW_EMOJI_DOM_ALT = "emoji";

/** 32x32, 16-colour PNG — 539 bytes. See the note above before regenerating. */
export const BW_EMOJI_SRC =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAAP1BMVEVUTcZPSbtOSLlOSbpOSLU+PrsEBPpycfQNDXx/f39/AH9cOaxRS8FmM8wAAABVVaoAAAAAAAAAAAAAAAAAAABIst5TAAAAEHRSTlP14l6bJAQBBQICAgewBQAD+rkRagAAAXtJREFUeNpdk4GyhCAIRVUQtXr4/3/7LlCb1oyzJccLXtik7zO1pMKqzFVrSdQbNtMCVKWUl6ep7IArFKJYmWTqF+BUno+WCJI7MBSyo7M2PRVq/EkRAkyJK5a07BJprbHkE1AqM0FfKX8Uqm3NPyLWcnEDXkFsKQx4vqSVPHegHgC6zilYh6JiwxdAoDB7Hf4+GJ7sgF2CDnMgCka8rz6MwVY6M4dcYSdfACXCGTQD2CEy4fQGeB9wrkTu+Fx6AYNv71G9vYz4+QHmATfcr9aIVMGd3xTRhig/3tzWF2i3K1g9wYzb1t9EhUC38/VKfr+nmQG4AAasWS89Xn0cHoXmAnXakGAbvzIrBOZTgxU0G+QJk+xxMddkPIBIpubHi3ditp9NDowYtSthUswKBMhS6gPYqHEcN5/QsPzGA/AW3TtS6K50B9Dp2pgL5ZRNShdAOhIQhDOCXm5MwgtIu9LJOFliWubyZ3IARZEn9tHdwgHMYom7njbQ3+cfGconJtGY8VsAAAAASUVORK5CYII=";

/** Exactly what one emoji contributes to a message body: 755 characters. */
export const BW_EMOJI_MARKDOWN = `![${BW_EMOJI_ALT}](${BW_EMOJI_SRC})`;

/**
 * The Birdwatch category for an emoji-mart `<Picker custom={…}>`. One definition for
 * every picker — the composer's and the reaction picker's — so the emoji looks and
 * searches (`bw`) the same wherever it is offered.
 */
export const BW_EMOJI_PICKER_CATEGORIES = [
  {
    id: "bw",
    name: "Birdwatch",
    emojis: [
      {
        id: "bw",
        name: "Birdwatch",
        keywords: ["bw"],
        skins: [{ src: BW_EMOJI_SRC }],
      },
    ],
  },
];
